// NPM
import React, { useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { isNull, omitBy, omit } from "lodash";

// UTILITY
import errorHandler from "Utils/error-handler";

// VALIDATOR
import { RestoreVersionSchema } from "./restore-version.validation";

// STYLE
import "../modal-forms/modal-forms.scss";

// HOOKS
import { useQuery } from "Hooks/index";

// ANT COMPONENTS
import { AlertBox, Button, DateRange } from "@life-line-lab/components-react";
import { Col, Row } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";
import { OrderSettingsEnum } from "Data/settings";

interface RestoreVersionProps {
  onSubmit: any;
  onCancel: any;
  info: any;
  tabKey: OrderSettingsEnum;
}

const RestoreVersion: React.FC<RestoreVersionProps> = (props) => {
  const { onSubmit, onCancel, info, tabKey } = props || {};

  const { t } = useTranslation();

  const initialInput = {
    ...info,
    ...info.data,
    validFrom: {
      date: null,
      dateString: "",
    },
    validTo: {
      date: null,
      dateString: "",
    },
  };
  const initialError = {};

  const [input, setInput] = useState(initialInput);
  const [error, setError] = useState<any>(initialError);

  const handleDateChange = (startDate: any, endDate: any) => {
    if (startDate.date) {
      setInput({ ...input, validFrom: startDate });
      setError({ initialError });
    } else if (endDate.date) {
      setInput({ ...input, validTo: endDate });
      setError({ initialError });
    }
  };

  const handleSubmit = () => {
    try {
      const valInput = {
        ...input,
        serviceTypes: input?.serviceTypes?.map((e) => e.code),
        deliveryTypes: input?.deliveryTypes?.map((deliveryType) => deliveryType.code),
        locations: input?.locations?.map((location) => location.code),
        validFrom: input.validFrom ? moment(input.validFrom.date).toISOString() : null,
        validTo: input.validTo ? moment(input.validTo.date).toISOString() : null,
      };

      const value = RestoreVersionSchema.validateSync(omitBy(valInput, isNull), {
        strict: false,
        abortEarly: true,
        stripUnknown: true,
        context: { isCustomerRequired: tabKey === OrderSettingsEnum.CUSTOMER_RATE_TABLE },
      });

      const finalValue = {
        ...value,
        isNewVersion: true,
        locationsData: input?.locations,
      };

      onSubmit(tabKey === OrderSettingsEnum.INSURANCE ? omit(finalValue, "name") : finalValue);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError({ ...error, [path]: t(`${value}`, { field: t(`label.${path}`) }) });
    }
  };

  return (
    <div className="modal-forms--restore-version">
      <Row>
        <Col className="mb-0">
          <WarningOutlined className="warning" />
        </Col>
        <Col className="mb-0">
          <ModuleTitleBar
            titleText={t("title.restoreVersion").toUpperCase()}
            haveSubtitle={false}
          />
        </Col>
      </Row>
      <Row>
        <Col className="mb-0">
          <p>{t("txt.restoreVersion")}</p>
        </Col>
      </Row>
      <Row gutter={[0, 15]}>
        <Col className="mb-0 full-width">
          <DateRange
            isRequired={true}
            startLabel={t("label.validFrom")}
            endLabel={t("label.validTo")}
            value={{
              startDate: input.validFrom,
              endDate: input.validTo,
            }}
            onChange={handleDateChange}
          />
          <AlertBox text={error.validFrom || error.validTo} />
        </Col>
      </Row>
      <Row className="mt-20 right">
        <Col className="mb-0">
          <Button
            type="primary"
            label={t("action.restore")}
            size="large"
            onClick={() => {
              handleSubmit();
            }}
          />
          <Button
            className="btn-cancel"
            type="secondary"
            size="large"
            label={t("action.cancel")}
            onClick={() => {
              onCancel();
            }}
          />
        </Col>
      </Row>
    </div>
  );
};

export default RestoreVersion;
